import { Button } from '@/components/home/buttons';
import { Card } from '@/components/home/card';
import { ClipboardCopy } from '@/components/home/clipboard-copy';
import { TeamSwitcher } from '@/components/home/team-switcher';
import { addPlayerToGame, setPlayerReadyState } from '@/firebase/firebase-db';
import type { ShiritoriGameStore } from '@/game/core/game.store';
import { getPlayerName } from '@/util/device-id';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface GameLobbyProps {
    game: ShiritoriGameStore;
}

export const GameLobby = observer(function ({ game }: GameLobbyProps) {
    const { t } = useTranslation();
    const gameId = useParams<{ gameId: string }>().gameId || '';

    const readyPlayers = game.turnSortedPlayers.filter(p => p.ready === true);
    const nonDevicePlayers = game.turnSortedPlayers.filter((p) => p.uid !== game.deviceId);
    const teamsReady = game.turnSortedPlayers.length > 0 && game.turnSortedPlayers.every((p) => p.team && p.ready === true);
    const numberOfTeams = Object.keys(game.teams).length;
    const expectedPlayers = game.config?.expectedPlayers;
    const isInGame = !!game.playerMap[game.deviceId];
    const currentNumberOfPlayers = game.turnSortedPlayers.length;

    useEffect(() => {
        const playerName = getPlayerName();

        // Do not auto-join if this device is already in the game
        if (isInGame) return;

        if (currentNumberOfPlayers === 0) {
            addPlayerToGame(gameId, playerName);
            return;
        }

        if (expectedPlayers && currentNumberOfPlayers < expectedPlayers) {
            addPlayerToGame(gameId, playerName);
        }
    }, [currentNumberOfPlayers, expectedPlayers, gameId, isInGame]);

    const copyUrl = `${window.location.origin}/join/${gameId}`;

    function setPlayerReady() {
        setPlayerReadyState(gameId, game.deviceId);
    }

    let readyButton = null;
    const deviceCanReady = numberOfTeams > 1 && !teamsReady && game.devicePlayer?.ready !== true;

    readyButton = (
        <Button hotkey={{ key: 'Enter', display: '⏎' }} disabled={!deviceCanReady} className="w-full" onClick={setPlayerReady}>
            {t('game.lobby.btnReady')} ({readyPlayers.length}/{game.config?.expectedPlayers})
        </Button>
    );

    return (
        <div className="w-full h-full flex items-center justify-center">
            <Card className="relative w-full max-w-lg shadow-2xl">
                <div className="flex flex-col items-center p-2 md:p-4">
                    {copyUrl && (
                        <>
                            <h3 className="text-sm md:text-base font-normal">{t('game.lobby.txtShareInvite')}</h3>
                            <ClipboardCopy className="my-3 w-4/5" text={copyUrl} displayText={gameId} />
                        </>
                    )}
                    <table className="w-full my-6 text-sm text-center">
                        <thead className="text-foreground border-b border-muted">
                            <tr>
                                <th className="w-1/2">{t('game.lobby.txtPlayer')}</th>
                                <th className="w-1/2 whitespace-nowrap">{t('game.lobby.txtTeam')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-muted">
                            {[game.devicePlayer, ...nonDevicePlayers].map((player, index) => (
                                <tr key={index} className={`select-none cursor-auto ${player?.ready === true ? 'opacity-50' : ''}`}>
                                    <td className="w-1/2">
                                        <span className={`${player?.uid === game.deviceId ? "font-bold before:mr-1 before:content-['·']" : ''} w-full mt-2 px-2 h-8 flex justify-center items-center`}>
                                            {player?.name}
                                        </span>
                                    </td>
                                    <td className="w-1/2 px-2">
                                        {player && (
                                            <TeamSwitcher gameId={gameId} player={player} />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {readyButton}
                </div>
            </Card>
        </div>
    );
});
